<template>
  <v-row no-gutters class="fill-height flex-column">
    <v-col cols="12" class="pr-6">
      <card-title-row translationPath="sidebar.preciseDigitizing.title" class="pt-3" />
      <precise-digitizing-summary v-model="currentCoordinates" @input="onInput" />
    </v-col>
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import { get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import PreciseDigitizingSummary from '@/components/PreciseDigitizingSummary';

import { transform } from 'ol/proj';
import { getDistanceBetweenCoords, getAzimuthOfCoords } from '@/assets/js/mapUtils';

export default {
  name: 'PreciseDigitizing',
  components: { CardTitleRow, PreciseDigitizingSummary },
  computed: {
    cursorPositionEpsg: get('map/cursorPositionEpsg'),
    preciseNewFeatureCoordinates: sync('edit/preciseNewFeatureCoordinates'),

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    currentCoordinates: [],
    currentNewLine: undefined,
  }),
  methods: {
    getCurrentCoordinates(coordinates = []) {
      const currentCoordinates = [];
      coordinates.forEach((coordinate, idx) => {
        const currentEpsgCoordinates = transform(coordinate, this.$_config.defaultEpsg, this.cursorPositionEpsg);
        const verticeObject = {
          type: 'vertice',
          coordinateX: Math.round(currentEpsgCoordinates[0] * 10000000000) / 10000000000,
          coordinateY: Math.round(currentEpsgCoordinates[1] * 10000000000) / 10000000000,
        };
        currentCoordinates.push(verticeObject);
        let lineObject;
        if (coordinates[idx + 1]) {
          const length = getDistanceBetweenCoords(coordinate, coordinates[idx + 1], this.$_config.defaultEpsg);
          const azimuth = getAzimuthOfCoords(coordinate, coordinates[idx + 1], this.$_config.defaultEpsg);
          lineObject = {
            type: 'line',
            length: Math.round(length * 1000) / 1000,
            azimuth: Math.round(azimuth * 100) / 100,
          };
        } else {
          lineObject = this.currentNewLine || {
            type: 'line',
            length: null,
            azimuth: null,
            new: true,
          };
        }
        currentCoordinates.push(lineObject);
      });
      if (!currentCoordinates?.length) {
        currentCoordinates.push({
          type: 'vertice',
          coordinateX: null,
          coordinateY: null,
          new: true,
        });
      }
      return currentCoordinates;
    },
    setCurrentMapCoordinates(coordinates) {
      if (coordinates.find(coordinate => coordinate[0] === null || coordinate[1] === null)) {
        return;
      }
      this.$root.$emit('setDrawingSidebarInteractionCoordinates', coordinates);
      if (!coordinates.length) {
        this.preciseNewFeatureCoordinates = [];
      }
    },
    onInput(e) {
      this.calculating = true;
      const newLineIndex = e.findIndex(element => element.type === 'line' && element.new);
      const newLine = e.find(element => element.type === 'line' && element.new);
      if (newLine && newLineIndex === e.length - 1) this.currentNewLine = newLine;
      else this.currentNewLine = undefined;
      this.setCurrentMapCoordinates(
        e
          .filter(element => element.type === 'vertice' && !element.new)
          .map(vertice => {
            const defaultEpsgCoordinates = transform(
              [vertice.coordinateX, vertice.coordinateY],
              this.cursorPositionEpsg,
              this.$_config.defaultEpsg
            );
            return [
              vertice.coordinateX === null ? null : defaultEpsgCoordinates[0],
              vertice.coordinateY === null ? null : defaultEpsgCoordinates[1],
            ];
          })
      );
    },
    preciseDigitizingAction() {
      this.$root.$emit('finishDrawingSidebarInteraction');
    },
    preciseDigitizingCancel() {
      this.$root.$emit('togglePreciseDigitizing', false);
    },
  },
  watch: {
    preciseNewFeatureCoordinates: {
      handler(nV, oV) {
        if ((nV && !oV) || (nV && oV && JSON.stringify(oV) !== JSON.stringify(nV))) {
          this.currentCoordinates = this.getCurrentCoordinates(nV);
        }
        if (!this.currentCoordinates?.length) {
          this.currentCoordinates = this.getCurrentCoordinates();
        }
      },
      deep: true,
      immediate: true,
    },
    cursorPositionEpsg(nV, oV) {
      this.currentCoordinates = this.currentCoordinates.map(element => {
        if (element.type === 'vertice') {
          const currentEpsgCoordinates = transform([element.coordinateX, element.coordinateY], oV, nV);
          element.coordinateX = Math.round(currentEpsgCoordinates[0] * 10000000000) / 10000000000;
          element.coordinateY = Math.round(currentEpsgCoordinates[1] * 10000000000) / 10000000000;
        }
        return element;
      });
    },
  },
  created() {
    this.onInput = debounce(this.onInput, 500);
  },
  mounted() {
    this.$root.$off('preciseDigitizingAction');
    this.$root.$on('preciseDigitizingAction', this.preciseDigitizingAction);
    this.$root.$off('preciseDigitizingCancel');
    this.$root.$on('preciseDigitizingCancel', this.preciseDigitizingCancel);
    this.stickyButtons = [
      {
        action: 'preciseDigitizingAction',
        translationPath: 'button.next',
      },
      {
        action: 'preciseDigitizingCancel',
        translationPath: 'button.cancel',
        cancel: true,
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = true;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.$root.$emit('togglePreciseDigitizing', false, true);
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
